/**
 * NATEX OPPORTUNITY SCORE - INDICATOR REGISTRY
 *
 * 51 indicators organized by pillar and factor with
 * professional-grade scoring thresholds.
 */

import {
  Indicator,
  Pillar,
  Factor,
  DataSource,
  ScoringThreshold
} from './types';

// ============================================================================
// HELPER: Create threshold ranges
// ============================================================================

const createThresholds = (
  ranges: Array<[number, number, number, string?]>
): ScoringThreshold[] => {
  return ranges.map(([minValue, maxValue, score, label]) => ({
    minValue,
    maxValue,
    score,
    label
  }));
};

// ============================================================================
// PILLAR 1: GROWTH DRIVERS (30%)
// ============================================================================

// Factor 1.1: Economic Momentum (10%)
const economicMomentumIndicators: Indicator[] = [
  {
    id: 'GDP_GROWTH_5Y',
    name: 'GDP Growth (5Y Avg)',
    description: 'Average annual real GDP growth rate over the past 5 years',
    pillar: Pillar.GROWTH_DRIVERS,
    factor: Factor.ECONOMIC_MOMENTUM,
    weight: 0.30,
    source: DataSource.WORLD_BANK,
    sourceCode: 'NY.GDP.MKTP.KD.ZG',
    unit: '%',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 1, 2, 'Stagnant'],
      [1, 3, 4, 'Slow'],
      [3, 5, 6, 'Moderate'],
      [5, 7, 8, 'Strong'],
      [7, Infinity, 10, 'Exceptional']
    ]),
    updateFrequency: 'annual'
  },
  {
    id: 'GDP_GROWTH_ACCELERATION',
    name: 'Growth Acceleration',
    description: 'Current GDP growth minus 3-year average (momentum indicator)',
    pillar: Pillar.GROWTH_DRIVERS,
    factor: Factor.ECONOMIC_MOMENTUM,
    weight: 0.20,
    source: DataSource.NATEX,
    unit: 'pp',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, -1, 2, 'Decelerating'],
      [-1, 0, 4, 'Slight slowdown'],
      [0, 1, 6, 'Stable'],
      [1, 2, 8, 'Accelerating'],
      [2, Infinity, 10, 'Strong acceleration']
    ]),
    updateFrequency: 'quarterly'
  },
  {
    id: 'GDP_GROWTH_STABILITY',
    name: 'Growth Stability',
    description: 'Standard deviation of GDP growth over 5 years (lower is better)',
    pillar: Pillar.GROWTH_DRIVERS,
    factor: Factor.ECONOMIC_MOMENTUM,
    weight: 0.20,
    source: DataSource.NATEX,
    unit: '%',
    isHigherBetter: false,
    thresholds: createThresholds([
      [5, Infinity, 2, 'Very volatile'],
      [3, 5, 4, 'Volatile'],
      [2, 3, 6, 'Moderate'],
      [1, 2, 8, 'Stable'],
      [-Infinity, 1, 10, 'Very stable']
    ]),
    updateFrequency: 'annual'
  },
  {
    id: 'SECTOR_DIVERSIFICATION',
    name: 'Sector Diversification',
    description: 'Herfindahl-Hirschman Index of GDP by sector (lower = more diverse)',
    pillar: Pillar.GROWTH_DRIVERS,
    factor: Factor.ECONOMIC_MOMENTUM,
    weight: 0.20,
    source: DataSource.WORLD_BANK,
    unit: 'index',
    isHigherBetter: false,
    thresholds: createThresholds([
      [0.5, Infinity, 2, 'Concentrated'],
      [0.4, 0.5, 4, 'High concentration'],
      [0.3, 0.4, 6, 'Moderate'],
      [0.2, 0.3, 8, 'Diversified'],
      [-Infinity, 0.2, 10, 'Highly diversified']
    ]),
    updateFrequency: 'annual'
  },
  {
    id: 'PRODUCTIVITY_GROWTH',
    name: 'Labor Productivity Growth',
    description: 'Annual growth in output per worker',
    pillar: Pillar.GROWTH_DRIVERS,
    factor: Factor.ECONOMIC_MOMENTUM,
    weight: 0.10,
    source: DataSource.WORLD_BANK,
    unit: '%',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, -2, 2, 'Declining'],
      [-2, 0, 4, 'Flat'],
      [0, 2, 6, 'Moderate'],
      [2, 4, 8, 'Strong'],
      [4, Infinity, 10, 'Exceptional']
    ]),
    updateFrequency: 'annual'
  }
];

// Factor 1.2: Demographic Dividend (10%)
const demographicIndicators: Indicator[] = [
  {
    id: 'WORKING_AGE_POP',
    name: 'Working Age Population',
    description: 'Percentage of population aged 15-64',
    pillar: Pillar.GROWTH_DRIVERS,
    factor: Factor.DEMOGRAPHIC_DIVIDEND,
    weight: 0.30,
    source: DataSource.UN_POPULATION,
    unit: '%',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 55, 2, 'Dependency burden'],
      [55, 60, 4, 'Low dividend'],
      [60, 65, 6, 'Moderate'],
      [65, 70, 8, 'Strong dividend'],
      [70, Infinity, 10, 'Peak dividend']
    ]),
    updateFrequency: 'annual'
  },
  {
    id: 'MEDIAN_AGE',
    name: 'Median Age (Sweet Spot)',
    description: 'Median age of population. Optimal range is 25-35 for growth.',
    pillar: Pillar.GROWTH_DRIVERS,
    factor: Factor.DEMOGRAPHIC_DIVIDEND,
    weight: 0.20,
    source: DataSource.UN_POPULATION,
    unit: 'years',
    isHigherBetter: true,
    thresholds: [
      { minValue: -Infinity, maxValue: 20, score: 4, label: 'Very young' },
      { minValue: 20, maxValue: 25, score: 7, label: 'Young' },
      { minValue: 25, maxValue: 35, score: 10, label: 'Sweet spot' },
      { minValue: 35, maxValue: 40, score: 7, label: 'Maturing' },
      { minValue: 40, maxValue: 45, score: 4, label: 'Mature' },
      { minValue: 45, maxValue: Infinity, score: 2, label: 'Aging' }
    ],
    updateFrequency: 'annual'
  },
  {
    id: 'POPULATION_GROWTH',
    name: 'Population Growth',
    description: 'Annual population growth rate. Optimal range is 1-2%.',
    pillar: Pillar.GROWTH_DRIVERS,
    factor: Factor.DEMOGRAPHIC_DIVIDEND,
    weight: 0.20,
    source: DataSource.WORLD_BANK,
    sourceCode: 'SP.POP.GROW',
    unit: '%',
    isHigherBetter: true,
    thresholds: [
      { minValue: -Infinity, maxValue: 0, score: 2, label: 'Declining' },
      { minValue: 0, maxValue: 0.5, score: 4, label: 'Stagnant' },
      { minValue: 0.5, maxValue: 1, score: 7, label: 'Moderate' },
      { minValue: 1, maxValue: 2, score: 10, label: 'Optimal' },
      { minValue: 2, maxValue: 3, score: 7, label: 'High' },
      { minValue: 3, maxValue: Infinity, score: 4, label: 'Very high' }
    ],
    updateFrequency: 'annual'
  },
  {
    id: 'URBANIZATION_RATE',
    name: 'Urbanization Rate',
    description: 'Urban population as % of total. Optimal for EM is 40-60%.',
    pillar: Pillar.GROWTH_DRIVERS,
    factor: Factor.DEMOGRAPHIC_DIVIDEND,
    weight: 0.20,
    source: DataSource.WORLD_BANK,
    sourceCode: 'SP.URB.TOTL.IN.ZS',
    unit: '%',
    isHigherBetter: true,
    thresholds: [
      { minValue: -Infinity, maxValue: 30, score: 4, label: 'Rural' },
      { minValue: 30, maxValue: 40, score: 7, label: 'Urbanizing' },
      { minValue: 40, maxValue: 60, score: 10, label: 'Sweet spot' },
      { minValue: 60, maxValue: 70, score: 7, label: 'Urban' },
      { minValue: 70, maxValue: Infinity, score: 4, label: 'Saturated' }
    ],
    updateFrequency: 'annual'
  },
  {
    id: 'DEPENDENCY_RATIO',
    name: 'Age Dependency Ratio',
    description: 'Ratio of dependents to working-age population (lower is better)',
    pillar: Pillar.GROWTH_DRIVERS,
    factor: Factor.DEMOGRAPHIC_DIVIDEND,
    weight: 0.10,
    source: DataSource.WORLD_BANK,
    sourceCode: 'SP.POP.DPND',
    unit: '%',
    isHigherBetter: false,
    thresholds: createThresholds([
      [80, Infinity, 2, 'High burden'],
      [70, 80, 4, 'Elevated'],
      [60, 70, 6, 'Moderate'],
      [50, 60, 8, 'Low'],
      [-Infinity, 50, 10, 'Favorable']
    ]),
    updateFrequency: 'annual'
  }
];

// Factor 1.3: Digital Readiness (10%)
const digitalIndicators: Indicator[] = [
  {
    id: 'INTERNET_PENETRATION',
    name: 'Internet Penetration',
    description: 'Percentage of population using the internet',
    pillar: Pillar.GROWTH_DRIVERS,
    factor: Factor.DIGITAL_READINESS,
    weight: 0.30,
    source: DataSource.ITU,
    unit: '%',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 20, 2, 'Very low'],
      [20, 40, 4, 'Low'],
      [40, 60, 6, 'Moderate'],
      [60, 80, 8, 'High'],
      [80, Infinity, 10, 'Very high']
    ]),
    updateFrequency: 'annual'
  },
  {
    id: 'MOBILE_SUBSCRIPTIONS',
    name: 'Mobile Subscriptions',
    description: 'Mobile cellular subscriptions per 100 people',
    pillar: Pillar.GROWTH_DRIVERS,
    factor: Factor.DIGITAL_READINESS,
    weight: 0.20,
    source: DataSource.ITU,
    unit: 'per 100',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 60, 2, 'Low'],
      [60, 80, 4, 'Moderate'],
      [80, 100, 6, 'Good'],
      [100, 120, 8, 'High'],
      [120, Infinity, 10, 'Saturated']
    ]),
    updateFrequency: 'annual'
  },
  {
    id: 'DIGITAL_PAYMENTS_GROWTH',
    name: 'Digital Payments Growth',
    description: 'Year-over-year growth in digital payment transactions',
    pillar: Pillar.GROWTH_DRIVERS,
    factor: Factor.DIGITAL_READINESS,
    weight: 0.20,
    source: DataSource.NATEX,
    unit: '%',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 5, 2, 'Slow'],
      [5, 15, 4, 'Moderate'],
      [15, 30, 6, 'Good'],
      [30, 50, 8, 'Strong'],
      [50, Infinity, 10, 'Exceptional']
    ]),
    updateFrequency: 'quarterly'
  },
  {
    id: 'TECH_SECTOR_SIZE',
    name: 'ICT Sector Size',
    description: 'ICT sector value added as % of GDP',
    pillar: Pillar.GROWTH_DRIVERS,
    factor: Factor.DIGITAL_READINESS,
    weight: 0.20,
    source: DataSource.WORLD_BANK,
    unit: '%',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 1, 2, 'Minimal'],
      [1, 3, 4, 'Small'],
      [3, 5, 6, 'Moderate'],
      [5, 8, 8, 'Significant'],
      [8, Infinity, 10, 'Major']
    ]),
    updateFrequency: 'annual'
  },
  {
    id: 'ECOMMERCE_PENETRATION',
    name: 'E-commerce Penetration',
    description: 'Online retail as % of total retail sales',
    pillar: Pillar.GROWTH_DRIVERS,
    factor: Factor.DIGITAL_READINESS,
    weight: 0.10,
    source: DataSource.NATEX,
    unit: '%',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 2, 2, 'Nascent'],
      [2, 5, 4, 'Emerging'],
      [5, 10, 6, 'Developing'],
      [10, 15, 8, 'Established'],
      [15, Infinity, 10, 'Advanced']
    ]),
    updateFrequency: 'annual'
  }
];

// ============================================================================
// PILLAR 2: INVESTMENT CLIMATE (25%)
// ============================================================================

// Factor 2.1: Policy Environment (10%)
const policyIndicators: Indicator[] = [
  {
    id: 'REGULATORY_QUALITY',
    name: 'Regulatory Quality',
    description: 'WGI Regulatory Quality percentile rank',
    pillar: Pillar.INVESTMENT_CLIMATE,
    factor: Factor.POLICY_ENVIRONMENT,
    weight: 0.30,
    source: DataSource.WGI,
    unit: 'percentile',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 25, 2, 'Poor'],
      [25, 50, 4, 'Weak'],
      [50, 65, 6, 'Fair'],
      [65, 80, 8, 'Good'],
      [80, Infinity, 10, 'Excellent']
    ]),
    updateFrequency: 'annual'
  },
  {
    id: 'GOVERNMENT_EFFECTIVENESS',
    name: 'Government Effectiveness',
    description: 'WGI Government Effectiveness percentile rank',
    pillar: Pillar.INVESTMENT_CLIMATE,
    factor: Factor.POLICY_ENVIRONMENT,
    weight: 0.30,
    source: DataSource.WGI,
    unit: 'percentile',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 25, 2, 'Poor'],
      [25, 50, 4, 'Weak'],
      [50, 65, 6, 'Fair'],
      [65, 80, 8, 'Good'],
      [80, Infinity, 10, 'Excellent']
    ]),
    updateFrequency: 'annual'
  },
  {
    id: 'POLICY_CONTINUITY',
    name: 'Policy Continuity',
    description: 'NATEX assessment of policy predictability and stability',
    pillar: Pillar.INVESTMENT_CLIMATE,
    factor: Factor.POLICY_ENVIRONMENT,
    weight: 0.20,
    source: DataSource.NATEX,
    unit: 'score',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 3, 2, 'Erratic'],
      [3, 5, 4, 'Unpredictable'],
      [5, 7, 6, 'Moderate'],
      [7, 8.5, 8, 'Stable'],
      [8.5, Infinity, 10, 'Very stable']
    ]),
    updateFrequency: 'quarterly'
  },
  {
    id: 'INVESTOR_PROTECTION',
    name: 'Investor Protection',
    description: 'Minority investor protection score',
    pillar: Pillar.INVESTMENT_CLIMATE,
    factor: Factor.POLICY_ENVIRONMENT,
    weight: 0.20,
    source: DataSource.B_READY,
    unit: 'score',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 40, 2, 'Weak'],
      [40, 55, 4, 'Fair'],
      [55, 70, 6, 'Moderate'],
      [70, 85, 8, 'Strong'],
      [85, Infinity, 10, 'Excellent']
    ]),
    updateFrequency: 'annual'
  }
];

// Factor 2.2: Market Access (8%)
const marketAccessIndicators: Indicator[] = [
  {
    id: 'MARKET_SIZE_GDP',
    name: 'Market Size (GDP)',
    description: 'Nominal GDP in current USD',
    pillar: Pillar.INVESTMENT_CLIMATE,
    factor: Factor.MARKET_ACCESS,
    weight: 0.25,
    source: DataSource.WORLD_BANK,
    sourceCode: 'NY.GDP.MKTP.CD',
    unit: 'USD bn',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 20, 2, 'Tiny'],
      [20, 50, 4, 'Small'],
      [50, 100, 6, 'Medium'],
      [100, 500, 8, 'Large'],
      [500, Infinity, 10, 'Very large']
    ]),
    updateFrequency: 'annual'
  },
  {
    id: 'TRADE_OPENNESS',
    name: 'Trade Openness',
    description: 'Total trade (exports + imports) as % of GDP',
    pillar: Pillar.INVESTMENT_CLIMATE,
    factor: Factor.MARKET_ACCESS,
    weight: 0.25,
    source: DataSource.WORLD_BANK,
    sourceCode: 'NE.TRD.GNFS.ZS',
    unit: '%',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 30, 2, 'Closed'],
      [30, 50, 4, 'Semi-closed'],
      [50, 70, 6, 'Moderate'],
      [70, 100, 8, 'Open'],
      [100, Infinity, 10, 'Very open']
    ]),
    updateFrequency: 'annual'
  },
  {
    id: 'FTA_NETWORK',
    name: 'FTA Network',
    description: 'Number of active Free Trade Agreements',
    pillar: Pillar.INVESTMENT_CLIMATE,
    factor: Factor.MARKET_ACCESS,
    weight: 0.25,
    source: DataSource.NATEX,
    unit: 'count',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 2, 2, 'Minimal'],
      [2, 5, 4, 'Limited'],
      [5, 10, 6, 'Moderate'],
      [10, 15, 8, 'Good'],
      [15, Infinity, 10, 'Extensive']
    ]),
    updateFrequency: 'annual'
  },
  {
    id: 'REGIONAL_HUB_POTENTIAL',
    name: 'Regional Hub Potential',
    description: 'Strategic location assessment for regional connectivity',
    pillar: Pillar.INVESTMENT_CLIMATE,
    factor: Factor.MARKET_ACCESS,
    weight: 0.25,
    source: DataSource.NATEX,
    unit: 'score',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 3, 2, 'Isolated'],
      [3, 5, 4, 'Peripheral'],
      [5, 7, 6, 'Connected'],
      [7, 9, 8, 'Hub'],
      [9, Infinity, 10, 'Major hub']
    ]),
    updateFrequency: 'annual'
  }
];

// Factor 2.3: Ease of Operations (7%)
const operationsIndicators: Indicator[] = [
  {
    id: 'DAYS_START_BUSINESS',
    name: 'Days to Start Business',
    description: 'Number of days to register a new business',
    pillar: Pillar.INVESTMENT_CLIMATE,
    factor: Factor.EASE_OF_OPERATIONS,
    weight: 0.30,
    source: DataSource.B_READY,
    unit: 'days',
    isHigherBetter: false,
    thresholds: createThresholds([
      [60, Infinity, 2, 'Very slow'],
      [30, 60, 4, 'Slow'],
      [15, 30, 6, 'Moderate'],
      [7, 15, 8, 'Fast'],
      [-Infinity, 7, 10, 'Very fast']
    ]),
    updateFrequency: 'annual'
  },
  {
    id: 'TAX_BURDEN',
    name: 'Total Tax Rate',
    description: 'Total tax rate as % of commercial profits',
    pillar: Pillar.INVESTMENT_CLIMATE,
    factor: Factor.EASE_OF_OPERATIONS,
    weight: 0.30,
    source: DataSource.B_READY,
    unit: '%',
    isHigherBetter: false,
    thresholds: createThresholds([
      [60, Infinity, 2, 'Very high'],
      [50, 60, 4, 'High'],
      [40, 50, 6, 'Moderate'],
      [30, 40, 8, 'Low'],
      [-Infinity, 30, 10, 'Very low']
    ]),
    updateFrequency: 'annual'
  },
  {
    id: 'INFRASTRUCTURE_QUALITY',
    name: 'Infrastructure Quality',
    description: 'Logistics Performance Index - Infrastructure component',
    pillar: Pillar.INVESTMENT_CLIMATE,
    factor: Factor.EASE_OF_OPERATIONS,
    weight: 0.25,
    source: DataSource.LPI,
    unit: 'index',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 2, 2, 'Poor'],
      [2, 2.5, 4, 'Fair'],
      [2.5, 3, 6, 'Moderate'],
      [3, 3.5, 8, 'Good'],
      [3.5, Infinity, 10, 'Excellent']
    ]),
    updateFrequency: 'annual'
  },
  {
    id: 'TERTIARY_ENROLLMENT',
    name: 'Talent Availability',
    description: 'Tertiary education enrollment rate',
    pillar: Pillar.INVESTMENT_CLIMATE,
    factor: Factor.EASE_OF_OPERATIONS,
    weight: 0.15,
    source: DataSource.WORLD_BANK,
    sourceCode: 'SE.TER.ENRR',
    unit: '%',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 10, 2, 'Very low'],
      [10, 25, 4, 'Low'],
      [25, 40, 6, 'Moderate'],
      [40, 60, 8, 'High'],
      [60, Infinity, 10, 'Very high']
    ]),
    updateFrequency: 'annual'
  }
];

// ============================================================================
// PILLAR 3: CATALYST PIPELINE (20%)
// ============================================================================

// Factor 3.1: Reform Momentum (8%)
const reformIndicators: Indicator[] = [
  {
    id: 'REFORM_COUNT_2Y',
    name: 'Reform Pace',
    description: 'Number of significant reforms enacted in past 2 years',
    pillar: Pillar.CATALYST_PIPELINE,
    factor: Factor.REFORM_MOMENTUM,
    weight: 0.40,
    source: DataSource.NATEX,
    unit: 'count',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 1, 2, 'Stagnant'],
      [1, 4, 4, 'Limited'],
      [4, 7, 6, 'Moderate'],
      [7, 10, 8, 'Active'],
      [10, Infinity, 10, 'Aggressive']
    ]),
    updateFrequency: 'quarterly'
  },
  {
    id: 'REFORM_QUALITY',
    name: 'Reform Quality',
    description: 'NATEX assessment of reform impact (1-10 scale)',
    pillar: Pillar.CATALYST_PIPELINE,
    factor: Factor.REFORM_MOMENTUM,
    weight: 0.35,
    source: DataSource.NATEX,
    unit: 'score',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 3, 2, 'Cosmetic'],
      [3, 5, 4, 'Limited'],
      [5, 7, 6, 'Moderate'],
      [7, 8.5, 8, 'Substantial'],
      [8.5, Infinity, 10, 'Transformational']
    ]),
    updateFrequency: 'quarterly'
  },
  {
    id: 'REFORM_PIPELINE',
    name: 'Reform Pipeline',
    description: 'Assessment of announced reforms pending implementation',
    pillar: Pillar.CATALYST_PIPELINE,
    factor: Factor.REFORM_MOMENTUM,
    weight: 0.25,
    source: DataSource.NATEX,
    unit: 'score',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 3, 2, 'Empty'],
      [3, 5, 4, 'Light'],
      [5, 7, 6, 'Moderate'],
      [7, 8.5, 8, 'Strong'],
      [8.5, Infinity, 10, 'Extensive']
    ]),
    updateFrequency: 'monthly'
  }
];

// Factor 3.2: FDI Pipeline (7%)
const fdiPipelineIndicators: Indicator[] = [
  {
    id: 'ANNOUNCED_FDI',
    name: 'Announced FDI Deals',
    description: 'Value of publicly announced FDI deals (USD billions)',
    pillar: Pillar.CATALYST_PIPELINE,
    factor: Factor.FDI_PIPELINE,
    weight: 0.45,
    source: DataSource.NATEX,
    unit: 'USD bn',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 0.5, 2, 'Minimal'],
      [0.5, 1, 4, 'Light'],
      [1, 2, 6, 'Moderate'],
      [2, 5, 8, 'Strong'],
      [5, Infinity, 10, 'Major']
    ]),
    updateFrequency: 'monthly'
  },
  {
    id: 'PIPELINE_CONVERSION',
    name: 'Pipeline Conversion Rate',
    description: '% of announced deals that are completed',
    pillar: Pillar.CATALYST_PIPELINE,
    factor: Factor.FDI_PIPELINE,
    weight: 0.30,
    source: DataSource.NATEX,
    unit: '%',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 10, 2, 'Very low'],
      [10, 30, 4, 'Low'],
      [30, 50, 6, 'Moderate'],
      [50, 70, 8, 'Good'],
      [70, Infinity, 10, 'Excellent']
    ]),
    updateFrequency: 'quarterly'
  },
  {
    id: 'INVESTOR_SENTIMENT',
    name: 'Investor Sentiment',
    description: 'Survey-based investor confidence index',
    pillar: Pillar.CATALYST_PIPELINE,
    factor: Factor.FDI_PIPELINE,
    weight: 0.25,
    source: DataSource.NATEX,
    unit: 'index',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 3, 2, 'Negative'],
      [3, 5, 4, 'Cautious'],
      [5, 7, 6, 'Neutral'],
      [7, 8.5, 8, 'Positive'],
      [8.5, Infinity, 10, 'Very positive']
    ]),
    updateFrequency: 'quarterly'
  }
];

// Factor 3.3: Event Calendar (5%)
const eventIndicators: Indicator[] = [
  {
    id: 'RATING_UPGRADE_POTENTIAL',
    name: 'Rating Upgrade Potential',
    description: 'Likelihood of credit rating upgrade in next 12 months',
    pillar: Pillar.CATALYST_PIPELINE,
    factor: Factor.EVENT_CALENDAR,
    weight: 0.40,
    source: DataSource.NATEX,
    unit: 'score',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 3, 2, 'Very unlikely'],
      [3, 5, 4, 'Unlikely'],
      [5, 7, 6, 'Possible'],
      [7, 8.5, 8, 'Likely'],
      [8.5, Infinity, 10, 'Very likely']
    ]),
    updateFrequency: 'monthly'
  },
  {
    id: 'ACCESSION_EVENTS',
    name: 'Accession Events',
    description: 'WTO/trade bloc membership progress',
    pillar: Pillar.CATALYST_PIPELINE,
    factor: Factor.EVENT_CALENDAR,
    weight: 0.35,
    source: DataSource.NATEX,
    unit: 'score',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 2, 2, 'No progress'],
      [2, 4, 4, 'Early stage'],
      [4, 6, 6, 'In progress'],
      [6, 8, 8, 'Advanced'],
      [8, Infinity, 10, 'Imminent']
    ]),
    updateFrequency: 'monthly'
  },
  {
    id: 'POLITICAL_CALENDAR',
    name: 'Political Calendar Impact',
    description: 'Expected impact of upcoming elections/transitions',
    pillar: Pillar.CATALYST_PIPELINE,
    factor: Factor.EVENT_CALENDAR,
    weight: 0.25,
    source: DataSource.NATEX,
    unit: 'score',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 3, 2, 'High risk'],
      [3, 5, 5, 'Elevated'],
      [5, 7, 7, 'Neutral'],
      [7, Infinity, 10, 'Favorable']
    ]),
    updateFrequency: 'monthly'
  }
];

// ============================================================================
// PILLAR 4: RISK PROFILE (15%)
// Note: For risk indicators, higher score = LOWER risk
// ============================================================================

// Factor 4.1: Political Stability (5%)
const politicalRiskIndicators: Indicator[] = [
  {
    id: 'POLITICAL_STABILITY_WGI',
    name: 'Political Stability Index',
    description: 'WGI Political Stability and Absence of Violence percentile',
    pillar: Pillar.RISK_PROFILE,
    factor: Factor.POLITICAL_STABILITY,
    weight: 0.40,
    source: DataSource.WGI,
    unit: 'percentile',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 25, 2, 'Very unstable'],
      [25, 40, 4, 'Unstable'],
      [40, 55, 6, 'Moderate'],
      [55, 70, 8, 'Stable'],
      [70, Infinity, 10, 'Very stable']
    ]),
    updateFrequency: 'annual'
  },
  {
    id: 'CONFLICT_RISK',
    name: 'Conflict Risk',
    description: 'Global Peace Index percentile (higher = more peaceful)',
    pillar: Pillar.RISK_PROFILE,
    factor: Factor.POLITICAL_STABILITY,
    weight: 0.35,
    source: DataSource.IEP,
    unit: 'percentile',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 25, 2, 'High conflict'],
      [25, 40, 4, 'Elevated'],
      [40, 55, 6, 'Moderate'],
      [55, 70, 8, 'Low'],
      [70, Infinity, 10, 'Very peaceful']
    ]),
    updateFrequency: 'annual'
  },
  {
    id: 'GEOPOLITICAL_RISK',
    name: 'Geopolitical Risk',
    description: 'NATEX assessment of geopolitical exposure',
    pillar: Pillar.RISK_PROFILE,
    factor: Factor.POLITICAL_STABILITY,
    weight: 0.25,
    source: DataSource.NATEX,
    unit: 'score',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 3, 2, 'Severe'],
      [3, 5, 4, 'High'],
      [5, 7, 6, 'Moderate'],
      [7, 8.5, 8, 'Low'],
      [8.5, Infinity, 10, 'Minimal']
    ]),
    updateFrequency: 'monthly'
  }
];

// Factor 4.2: Macro Stability (5%)
const macroRiskIndicators: Indicator[] = [
  {
    id: 'INFLATION_CPI',
    name: 'Inflation',
    description: 'Consumer price inflation, annual %',
    pillar: Pillar.RISK_PROFILE,
    factor: Factor.MACRO_STABILITY,
    weight: 0.40,
    source: DataSource.WORLD_BANK,
    sourceCode: 'FP.CPI.TOTL.ZG',
    unit: '%',
    isHigherBetter: false,
    thresholds: createThresholds([
      [15, Infinity, 2, 'Very high'],
      [10, 15, 4, 'High'],
      [7, 10, 6, 'Elevated'],
      [5, 7, 8, 'Moderate'],
      [-Infinity, 5, 10, 'Low']
    ]),
    updateFrequency: 'monthly'
  },
  {
    id: 'CURRENCY_VOLATILITY',
    name: 'Currency Volatility',
    description: '1-year FX volatility (annualized)',
    pillar: Pillar.RISK_PROFILE,
    factor: Factor.MACRO_STABILITY,
    weight: 0.35,
    source: DataSource.NATEX,
    unit: '%',
    isHigherBetter: false,
    thresholds: createThresholds([
      [30, Infinity, 2, 'Very volatile'],
      [20, 30, 4, 'Volatile'],
      [15, 20, 6, 'Moderate'],
      [10, 15, 8, 'Stable'],
      [-Infinity, 10, 10, 'Very stable']
    ]),
    updateFrequency: 'monthly'
  },
  {
    id: 'EXTERNAL_DEBT_GDP',
    name: 'External Debt',
    description: 'External debt as % of GDP',
    pillar: Pillar.RISK_PROFILE,
    factor: Factor.MACRO_STABILITY,
    weight: 0.25,
    source: DataSource.WORLD_BANK,
    sourceCode: 'DT.DOD.DECT.GN.ZS',
    unit: '%',
    isHigherBetter: false,
    thresholds: createThresholds([
      [80, Infinity, 2, 'Very high'],
      [60, 80, 4, 'High'],
      [50, 60, 6, 'Moderate'],
      [40, 50, 8, 'Low'],
      [-Infinity, 40, 10, 'Very low']
    ]),
    updateFrequency: 'annual'
  }
];

// Factor 4.3: External Vulnerability (5%)
const externalRiskIndicators: Indicator[] = [
  {
    id: 'RESERVE_ADEQUACY',
    name: 'Reserve Adequacy',
    description: 'Foreign reserves in months of import cover',
    pillar: Pillar.RISK_PROFILE,
    factor: Factor.EXTERNAL_VULNERABILITY,
    weight: 0.40,
    source: DataSource.IMF,
    unit: 'months',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 2, 2, 'Critical'],
      [2, 4, 4, 'Low'],
      [4, 6, 6, 'Adequate'],
      [6, 8, 8, 'Strong'],
      [8, Infinity, 10, 'Very strong']
    ]),
    updateFrequency: 'monthly'
  },
  {
    id: 'CURRENT_ACCOUNT',
    name: 'Current Account Balance',
    description: 'Current account balance as % of GDP',
    pillar: Pillar.RISK_PROFILE,
    factor: Factor.EXTERNAL_VULNERABILITY,
    weight: 0.35,
    source: DataSource.IMF,
    unit: '% of GDP',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, -6, 2, 'Large deficit'],
      [-6, -3, 4, 'Deficit'],
      [-3, 0, 6, 'Moderate deficit'],
      [0, 3, 8, 'Surplus'],
      [3, Infinity, 10, 'Strong surplus']
    ]),
    updateFrequency: 'quarterly'
  },
  {
    id: 'SHORT_TERM_DEBT_RESERVES',
    name: 'Short-term Debt Coverage',
    description: 'Short-term external debt as % of reserves',
    pillar: Pillar.RISK_PROFILE,
    factor: Factor.EXTERNAL_VULNERABILITY,
    weight: 0.25,
    source: DataSource.WORLD_BANK,
    unit: '%',
    isHigherBetter: false,
    thresholds: createThresholds([
      [80, Infinity, 2, 'Critical'],
      [60, 80, 4, 'High'],
      [50, 60, 6, 'Elevated'],
      [30, 50, 8, 'Moderate'],
      [-Infinity, 30, 10, 'Low']
    ]),
    updateFrequency: 'annual'
  }
];

// ============================================================================
// PILLAR 5: VALUATION SIGNAL (10%)
// ============================================================================

// Factor 5.1: Relative Valuation (5%)
const valuationIndicators: Indicator[] = [
  {
    id: 'FDI_GDP_VS_PEERS',
    name: 'FDI Relative to Peers',
    description: 'FDI inflows/GDP relative to peer country average (%)',
    pillar: Pillar.VALUATION_SIGNAL,
    factor: Factor.RELATIVE_VALUATION,
    weight: 0.40,
    source: DataSource.NATEX,
    unit: '%',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 50, 2, 'Underperforming'],
      [50, 80, 4, 'Below average'],
      [80, 120, 6, 'Average'],
      [120, 150, 8, 'Above average'],
      [150, Infinity, 10, 'Outperforming']
    ]),
    updateFrequency: 'annual'
  },
  {
    id: 'GROWTH_VS_RATING',
    name: 'Growth vs Rating',
    description: 'GDP growth percentile minus credit rating percentile (underrated = positive)',
    pillar: Pillar.VALUATION_SIGNAL,
    factor: Factor.RELATIVE_VALUATION,
    weight: 0.35,
    source: DataSource.NATEX,
    unit: 'pp',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, -15, 2, 'Overrated'],
      [-15, -5, 4, 'Slight overrated'],
      [-5, 5, 6, 'Fairly rated'],
      [5, 15, 8, 'Underrated'],
      [15, Infinity, 10, 'Strongly underrated']
    ]),
    updateFrequency: 'annual'
  },
  {
    id: 'COST_ADVANTAGE',
    name: 'Cost Advantage',
    description: 'Labor cost vs productivity ratio (lower = better value)',
    pillar: Pillar.VALUATION_SIGNAL,
    factor: Factor.RELATIVE_VALUATION,
    weight: 0.25,
    source: DataSource.NATEX,
    unit: 'index',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 3, 2, 'Expensive'],
      [3, 5, 4, 'Moderate'],
      [5, 7, 6, 'Good value'],
      [7, 8.5, 8, 'Strong value'],
      [8.5, Infinity, 10, 'Exceptional value']
    ]),
    updateFrequency: 'annual'
  }
];

// Factor 5.2: Timing Indicator (5%)
const timingIndicators: Indicator[] = [
  {
    id: 'SCORE_MOMENTUM',
    name: 'Score Momentum',
    description: 'Direction of NATEX score over last 3 periods',
    pillar: Pillar.VALUATION_SIGNAL,
    factor: Factor.TIMING_INDICATOR,
    weight: 0.40,
    source: DataSource.NATEX,
    unit: 'trend',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, -2, 2, 'Strong decline'],
      [-2, 0, 4, 'Declining'],
      [0, 2, 6, 'Stable'],
      [2, 5, 8, 'Rising'],
      [5, Infinity, 10, 'Strong rise']
    ]),
    updateFrequency: 'monthly'
  },
  {
    id: 'CATALYST_PROXIMITY',
    name: 'Catalyst Proximity',
    description: 'Days until next major positive catalyst',
    pillar: Pillar.VALUATION_SIGNAL,
    factor: Factor.TIMING_INDICATOR,
    weight: 0.40,
    source: DataSource.NATEX,
    unit: 'days',
    isHigherBetter: false,
    thresholds: createThresholds([
      [365, Infinity, 2, 'Distant'],
      [180, 365, 4, 'Long-term'],
      [90, 180, 6, 'Medium-term'],
      [30, 90, 8, 'Near-term'],
      [-Infinity, 30, 10, 'Imminent']
    ]),
    updateFrequency: 'weekly'
  },
  {
    id: 'INVESTOR_POSITIONING',
    name: 'Investor Positioning',
    description: 'Contrarian signal (underweighted by market = opportunity)',
    pillar: Pillar.VALUATION_SIGNAL,
    factor: Factor.TIMING_INDICATOR,
    weight: 0.20,
    source: DataSource.NATEX,
    unit: 'score',
    isHigherBetter: true,
    thresholds: createThresholds([
      [-Infinity, 3, 2, 'Crowded'],
      [3, 5, 5, 'Neutral'],
      [5, 7, 7, 'Underweight'],
      [7, Infinity, 10, 'Contrarian']
    ]),
    updateFrequency: 'monthly'
  }
];

// ============================================================================
// COMBINED INDICATOR REGISTRY
// ============================================================================

export const ALL_INDICATORS: Indicator[] = [
  // Pillar 1: Growth Drivers
  ...economicMomentumIndicators,
  ...demographicIndicators,
  ...digitalIndicators,
  // Pillar 2: Investment Climate
  ...policyIndicators,
  ...marketAccessIndicators,
  ...operationsIndicators,
  // Pillar 3: Catalyst Pipeline
  ...reformIndicators,
  ...fdiPipelineIndicators,
  ...eventIndicators,
  // Pillar 4: Risk Profile
  ...politicalRiskIndicators,
  ...macroRiskIndicators,
  ...externalRiskIndicators,
  // Pillar 5: Valuation Signal
  ...valuationIndicators,
  ...timingIndicators
];

// Create lookup maps for fast access
export const INDICATOR_BY_ID = new Map<string, Indicator>(
  ALL_INDICATORS.map(ind => [ind.id, ind])
);

export const INDICATORS_BY_PILLAR = new Map<Pillar, Indicator[]>(
  Object.values(Pillar).map(pillar => [
    pillar,
    ALL_INDICATORS.filter(ind => ind.pillar === pillar)
  ])
);

export const INDICATORS_BY_FACTOR = new Map<Factor, Indicator[]>(
  Object.values(Factor).map(factor => [
    factor,
    ALL_INDICATORS.filter(ind => ind.factor === factor)
  ])
);

// Export total indicator count for validation
export const TOTAL_INDICATORS = ALL_INDICATORS.length;
// Should be 51 indicators

// Log indicator count for debugging
console.log(`NATEX Score: ${TOTAL_INDICATORS} indicators loaded`);
